import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Trophy, Medal, Award, Star, TrendingUp, Eye, Heart,
  Calendar, Clock, ChevronRight, Crown, Flame, Gift
} from 'lucide-react';
import { getCurrentWeekPrizes, getComics } from '../services/api';
import { SEO } from '../components/SEO';
import { PrizeLeaderboard } from '../components/PrizeSystem';

const PRIZE_TIERS = [
  { 
    position: 1, 
    icon: Crown, 
    color: 'text-[#FFB703]', 
    bgColor: 'from-[#FFB703]/20 to-[#FFB703]/5',
    prize: 100,
    label: '1st Place'
  },
  { 
    position: 2, 
    icon: Medal, 
    color: 'text-gray-400', 
    bgColor: 'from-gray-400/20 to-gray-400/5',
    prize: 50,
    label: '2nd Place'
  },
  { 
    position: 3, 
    icon: Award, 
    color: 'text-amber-600', 
    bgColor: 'from-amber-600/20 to-amber-600/5',
    prize: 25,
    label: '3rd Place'
  },
];

const CATEGORIES = [
  { id: 'most_views', name: 'Most Viewed', icon: Eye, prize: 100 },
  { id: 'most_likes', name: 'Most Liked', icon: Heart, prize: 75 },
  { id: 'rising_star', name: 'Rising Star', icon: TrendingUp, prize: 50 },
  { id: 'editors_pick', name: "Editor's Pick", icon: Star, prize: 50 },
];

function PrizesPage() {
  const [prizes, setPrizes] = useState(null);
  const [topComics, setTopComics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0 });

  useEffect(() => {
    fetchData();
    updateCountdown();
    const timer = setInterval(updateCountdown, 60000);
    return () => clearInterval(timer);
  }, []);

  const fetchData = async () => {
    try {
      const [prizesRes, comicsRes] = await Promise.all([
        getCurrentWeekPrizes(),
        getComics({ sort: 'views', order: 'desc', limit: 3 })
      ]);
      setPrizes(prizesRes.data);
      setTopComics(comicsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateCountdown = () => {
    const now = new Date();
    const endOfWeek = new Date();
    endOfWeek.setDate(now.getDate() + (7 - now.getDay()));
    endOfWeek.setHours(23, 59, 59, 999);
    
    const diff = endOfWeek - now;
    setTimeLeft({
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
    });
  };

  return (
    <div className="min-h-screen pb-24" data-testid="prizes-page">
      <SEO 
        title="Weekly Prizes" 
        description="Win cash prizes every week! Top comics earn rewards for views, likes, and editorial picks."
      />

      {/* Hero Section */}
      <section className="relative bg-[#0a0a0a] py-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#FFB703]/20 blur-[100px] rounded-full" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#E63946]/20 blur-[100px] rounded-full" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 relative">
          <div className="text-center mb-12">
            <div className="w-20 h-20 rounded-full bg-[#FFB703]/20 flex items-center justify-center mx-auto mb-6">
              <Trophy className="text-[#FFB703]" size={40} />
            </div>
            <h1 className="text-5xl md:text-6xl font-teko text-white mb-4">
              WEEKLY <span className="text-[#FFB703]">PRIZES</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Create amazing comics and win cash prizes every week!
            </p>
          </div>

          {/* Countdown Timer */}
          <div className="flex justify-center gap-4 mb-12">
            {[
              { value: timeLeft.days, label: 'Days' },
              { value: timeLeft.hours, label: 'Hours' },
              { value: timeLeft.minutes, label: 'Minutes' },
            ].map((item) => (
              <div key={`countdown-${item.label}`} className="text-center">
                <div className="w-20 h-20 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/20">
                  <span className="text-4xl font-teko text-white">{item.value}</span>
                </div>
                <p className="text-sm text-gray-500 mt-2">{item.label}</p>
              </div>
            ))}
          </div>

          {/* Prize Pool */}
          <div className="bg-gradient-to-r from-[#FFB703]/10 to-[#E63946]/10 backdrop-blur-sm rounded-2xl p-8 border border-[#FFB703]/30 max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <p className="text-sm text-gray-400 uppercase tracking-wider mb-2">Total Prize Pool</p>
              <p className="text-6xl font-teko text-[#FFB703]">${prizes?.total_pool || 275}</p>
            </div>

            {/* Categories */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {CATEGORIES.map(cat => {
                const Icon = cat.icon;
                return (
                  <div key={cat.id} className="text-center p-4 bg-white/5 rounded-xl">
                    <Icon className="mx-auto mb-2 text-[#FFB703]" size={24} />
                    <p className="text-sm text-white font-medium">{cat.name}</p>
                    <p className="text-[#FFB703] font-bold">${cat.prize}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Current Leaders */}
      <section className="page-padding py-16">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-sm text-[var(--primary)] uppercase tracking-wider mb-2">Current Week</p>
            <h2 className="text-4xl font-teko">Leading Comics</h2>
          </div>

          {/* Top 3 Podium */}
          {topComics.length >= 3 && (
            <div className="flex items-end justify-center gap-4 mb-12">
              {/* 2nd Place */}
              <div className="text-center">
                <Link to={`/comic/${topComics[1]?.id}`} className="block group">
                  <div className="relative">
                    <img 
                      src={topComics[1]?.cover_image}
                      alt={topComics[1]?.title}
                      className="w-32 h-44 object-cover rounded-xl mx-auto mb-3 group-hover:scale-105 transition-transform"
                    />
                    <div className="absolute -top-3 -right-3 w-10 h-10 bg-gray-400 rounded-full flex items-center justify-center text-black font-bold">
                      2
                    </div>
                  </div>
                  <p className="font-medium line-clamp-1">{topComics[1]?.title}</p>
                  <p className="text-sm text-[var(--text-secondary)]">${PRIZE_TIERS[1].prize}</p>
                </Link>
              </div>

              {/* 1st Place */}
              <div className="text-center -mb-8">
                <Link to={`/comic/${topComics[0]?.id}`} className="block group">
                  <div className="relative">
                    <Crown className="text-[#FFB703] mx-auto mb-2" size={32} />
                    <img 
                      src={topComics[0]?.cover_image}
                      alt={topComics[0]?.title}
                      className="w-40 h-56 object-cover rounded-xl mx-auto mb-3 ring-4 ring-[#FFB703] group-hover:scale-105 transition-transform"
                    />
                    <div className="absolute -top-3 -right-3 w-12 h-12 bg-[#FFB703] rounded-full flex items-center justify-center text-black font-bold text-xl">
                      1
                    </div>
                  </div>
                  <p className="font-teko text-xl">{topComics[0]?.title}</p>
                  <p className="text-[#FFB703] font-bold">${PRIZE_TIERS[0].prize}</p>
                </Link>
              </div>

              {/* 3rd Place */}
              <div className="text-center">
                <Link to={`/comic/${topComics[2]?.id}`} className="block group">
                  <div className="relative">
                    <img 
                      src={topComics[2]?.cover_image}
                      alt={topComics[2]?.title}
                      className="w-32 h-44 object-cover rounded-xl mx-auto mb-3 group-hover:scale-105 transition-transform"
                    />
                    <div className="absolute -top-3 -right-3 w-10 h-10 bg-amber-600 rounded-full flex items-center justify-center text-white font-bold">
                      3
                    </div>
                  </div>
                  <p className="font-medium line-clamp-1">{topComics[2]?.title}</p>
                  <p className="text-sm text-[var(--text-secondary)]">${PRIZE_TIERS[2].prize}</p>
                </Link>
              </div>
            </div>
          )}

          {/* Full Leaderboard */}
          <div className="max-w-3xl mx-auto">
            <PrizeLeaderboard />
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-[var(--surface)] py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-teko">How It Works</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[var(--primary)]/10 flex items-center justify-center mx-auto mb-4">
                <Flame className="text-[var(--primary)]" size={28} />
              </div>
              <h3 className="font-teko text-xl mb-2">1. Create Great Content</h3>
              <p className="text-[var(--text-secondary)] text-sm">
                Publish engaging comics that readers love. Quality content gets more views and likes.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#FFB703]/10 flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="text-[#FFB703]" size={28} />
              </div>
              <h3 className="font-teko text-xl mb-2">2. Climb the Ranks</h3>
              <p className="text-[var(--text-secondary)] text-sm">
                Your comic's performance is tracked weekly. Top performers in each category win prizes.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
                <Gift className="text-green-500" size={28} />
              </div>
              <h3 className="font-teko text-xl mb-2">3. Win Cash Prizes</h3>
              <p className="text-[var(--text-secondary)] text-sm">
                Winners are announced every Sunday. Prizes are automatically added to your earnings.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 page-padding">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-teko mb-4">Ready to Win?</h2>
          <p className="text-[var(--text-secondary)] mb-8">
            Start creating today and compete for weekly prizes!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/create"
              className="px-8 py-3 bg-[var(--primary)] text-white rounded-lg hover:bg-[var(--primary)]/90 transition-colors font-medium"
            >
              Start Creating
            </Link>
            <Link 
              to="/leaderboard"
              className="px-8 py-3 border border-[var(--border)] rounded-lg hover:border-[var(--primary)] transition-colors"
            >
              View Full Leaderboard
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default PrizesPage;
